import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { folderService } from '@/utilities/folder/FolderService';
import { PublicFolder, PublicFolderBookmark } from '@/utilities/types/Folder';
import AppDownloadCTA from '@/components/public/AppDownloadCTA';
import { useSflColors, createSflTextStyles, SflCard, sflRadius } from '@/styling/sfl';

const ICON_MAP: Record<string, string> = {
  folder: '📁',
  briefcase: '💼',
  book: '📚',
  palette: '🎨',
  'cart-shopping': '🛒',
  newspaper: '📰',
  utensils: '🍴',
  plane: '✈️',
  'laptop-code': '💻',
  heart: '❤️',
  star: '⭐',
  tag: '🏷️',
  bookmark: '🔖',
  file: '📄',
  image: '🖼️',
  video: '🎥',
};

function getIconEmoji(iconName?: string): string {
  return ICON_MAP[iconName || 'folder'] || '📁';
}

function BookmarkCard({ b }: { b: PublicFolderBookmark }) {
  const colors = useSflColors();
  const text = useMemo(() => createSflTextStyles(colors), [colors]);
  const host = b.url
    ? (() => {
        try {
          return new URL(b.url).hostname.replace('www.', '');
        } catch {
          return '';
        }
      })()
    : '';

  return (
    <a
      href={b.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex gap-4 transition-shadow hover:shadow-md"
      style={{
        padding: 16,
        borderRadius: sflRadius.lg,
        border: `1px solid ${colors.border2}`,
        backgroundColor: colors.surface,
        textDecoration: 'none',
        color: colors.text,
      }}
    >
      {b.image ? (
        <img
          src={b.image}
          alt=""
          className="w-16 h-16 object-cover flex-shrink-0"
          style={{ borderRadius: sflRadius.sm }}
        />
      ) : (
        <div
          className="w-16 h-16 flex items-center justify-center flex-shrink-0 text-2xl"
          style={{ borderRadius: sflRadius.sm, backgroundColor: colors.surface2 }}
        >
          🔖
        </div>
      )}
      <div className="min-w-0 flex-1">
        <h3 className="line-clamp-2 font-medium" style={text.title}>
          {b.title || b.url || 'Untitled'}
        </h3>
        {host && (
          <p className="text-xs mt-0.5" style={{ color: colors.text3 }}>
            {host}
          </p>
        )}
        {b.description && (
          <p className="text-sm line-clamp-2 mt-1" style={{ ...text.body, color: colors.text2 }}>
            {b.description}
          </p>
        )}
      </div>
    </a>
  );
}

export default function PublicFolderDetailScreen() {
  const colors = useSflColors();
  const text = useMemo(() => createSflTextStyles(colors), [colors]);
  const { slug } = useParams<{ slug: string }>();
  const [folder, setFolder] = useState<PublicFolder | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    let cancelled = false;
    folderService.getPublicFolderBySlug(slug).then((data) => {
      if (cancelled) return;
      setFolder(data);
      setNotFound(!data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.bg }}>
        <div
          className="animate-spin rounded-full h-10 w-10 border-2 border-t-transparent"
          style={{ borderColor: `${colors.accent} transparent transparent transparent` }}
        />
      </div>
    );
  }

  if (notFound || !folder) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-4"
        style={{ backgroundColor: colors.bg, color: colors.text }}
      >
        <h1 className="text-xl font-semibold mb-2" style={{ fontFamily: 'var(--sfl-font-sans)' }}>
          Folder not found
        </h1>
        <Link to="/p/folders" className="hover:underline" style={{ color: colors.accent }}>
          View all public folders
        </Link>
        <AppDownloadCTA className="mt-8" utm={{ utm_content: 'not_found', utm_source: 'public_folder_detail' }} />
      </div>
    );
  }

  const color = folder.color || colors.accent;
  const bookmarks = folder.bookmarks ?? [];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bg, color: colors.text }}>
      <header style={{ borderBottom: `1px solid ${colors.border2}` }}>
        <div className="max-w-3xl mx-auto px-4 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/p/folders" className="text-sm hover:opacity-80" style={{ color: colors.text2 }}>
              ← All folders
            </Link>
            <span style={{ color: colors.border }}>|</span>
            <span className="text-xl font-semibold" style={{ color: colors.accent, fontFamily: 'var(--sfl-font-sans)' }}>
              Save for Later
            </span>
          </div>
          <AppDownloadCTA utm={{ utm_content: 'header', utm_source: 'public_folder_detail' }} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="flex items-start gap-4 mb-8">
          <div
            className="w-14 h-14 flex items-center justify-center text-white text-2xl flex-shrink-0"
            style={{ backgroundColor: color, borderRadius: sflRadius.lg }}
          >
            {getIconEmoji(folder.icon)}
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold" style={{ ...text.marketingHeadline }}>
              {folder.name}
            </h1>
            {folder.description && (
              <p className="mt-1" style={{ ...text.body, color: colors.text2 }}>
                {folder.description}
              </p>
            )}
            {folder.bookmarkCount != null && (
              <p className="text-sm mt-2" style={{ color: colors.text3 }}>
                {folder.bookmarkCount} {folder.bookmarkCount === 1 ? 'bookmark' : 'bookmarks'}
              </p>
            )}
          </div>
        </div>

        {bookmarks.length > 0 ? (
          <ul className="space-y-3">
            {bookmarks.map((b) => (
              <li key={b.id}>
                <BookmarkCard b={b} />
              </li>
            ))}
          </ul>
        ) : (
          <SflCard className="py-12 text-center" style={{ color: colors.text2 }}>
            No bookmarks in this list yet.
          </SflCard>
        )}

        <section
          className="mt-14 text-center py-10"
          style={{
            borderRadius: sflRadius.lg,
            backgroundColor: colors.accentLight,
            border: `1px solid ${colors.border2}`,
          }}
        >
          <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: 'var(--sfl-font-sans)', color: colors.text }}>
            Create your own lists
          </h2>
          <p className="mb-6 max-w-md mx-auto" style={{ ...text.body, color: colors.text2 }}>
            Download the app to save links and organize them into folders.
          </p>
          <AppDownloadCTA utm={{ utm_content: 'footer', utm_source: 'public_folder_detail' }} />
        </section>
      </main>
    </div>
  );
}
